import * as ReachDialog from '@reach/dialog';
import { animated, config, useTransition } from '@react-spring/web';
import { type ComponentProps, memo } from 'react';

import { cn } from '@air/utils-tailwind';

export type ModalSize = 'small' | 'medium' | 'large';

const MODAL_SIZE_CLASS_NAME: { [key in ModalSize]: string } = {
  large: 'w-[800px]',
  medium: 'w-[496px]',
  small: 'w-[400px]',
};

const AnimatedOverlay = animated(ReachDialog.DialogOverlay);
const AnimatedContent = animated(ReachDialog.DialogContent);

export type ModalProps = Pick<
  ReachDialog.DialogOverlayProps,
  | 'allowPinchZoom'
  | 'dangerouslyBypassFocusLock'
  | 'dangerouslyBypassScrollLock'
  | 'initialFocusRef'
  | 'isOpen'
  | 'onDismiss'
  | 'unstable_lockFocusAcrossFrames'
> &
  ReachDialog.DialogContentProps &
  Omit<ComponentProps<typeof AnimatedContent>, 'className'> &
  Pick<ComponentProps<'div'>, 'className'> & {
    overlayClassName?: string;
    size?: ModalSize;
  };

export const Modal = memo(
  ({
    allowPinchZoom,
    children,
    className,
    dangerouslyBypassFocusLock,
    dangerouslyBypassScrollLock,
    initialFocusRef,
    isOpen,
    onDismiss,
    overlayClassName,
    size = 'medium',
    unstable_lockFocusAcrossFrames,
    ...restOfProps
  }: ModalProps) => {
    const transitions = useTransition(isOpen, {
      config: config.stiff,
      enter: { opacity: 1, y: 0 },
      from: { opacity: 0, y: 50 },
      leave: { opacity: 0, y: 10 },
    });

    return transitions(
      (styles, item) =>
        item && (
          <AnimatedOverlay
            allowPinchZoom={allowPinchZoom}
            className={cn(
              'fixed inset-0 z-portal overflow-auto bg-black/75 py-8 transition-opacity',
              isOpen ? 'opacity-100' : 'opacity-0',
              overlayClassName,
            )}
            dangerouslyBypassFocusLock={dangerouslyBypassFocusLock}
            dangerouslyBypassScrollLock={dangerouslyBypassScrollLock}
            initialFocusRef={initialFocusRef}
            onDismiss={onDismiss}
            style={{ opacity: styles.opacity }}
            unstable_lockFocusAcrossFrames={unstable_lockFocusAcrossFrames}
          >
            <AnimatedContent
              className={cn(
                'relative mx-auto mt-[calc(10vw-32px)] min-h-[100px] max-w-[100vw] rounded-6 bg-surface-1 p-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-9',
                MODAL_SIZE_CLASS_NAME[size],
                className,
              )}
              style={{ transform: styles.y.to((value) => `translate3d(0px, ${value}px, 0px)`) }}
              {...restOfProps}
            >
              {children}
            </AnimatedContent>
          </AnimatedOverlay>
        ),
    );
  },
);

Modal.displayName = 'Modal';
